import React, { useEffect, useMemo, useState } from 'react';
import { ChevronDown, Clock, History, Loader2, RefreshCw, ShieldCheck, User } from 'lucide-react';
import { collection, db, onSnapshot, query, where } from '../firebase';
import { cn } from '../lib/utils';
import { type ActivityLocale, translateActivityEvent } from '../lib/activityEventTranslator';

interface EntityActionLogProps {
  orgId: string;
  entityId: string;
  entityType?: string;
  className?: string;
  locale?: ActivityLocale;
  compact?: boolean;
}

interface ActionLogRecord {
  id: string;
  action: unknown;
  module: string;
  targetName?: string;
  userName: string;
  userEmail: string;
  timestamp: Date;
  metadata: Record<string, unknown>;
}

const CATEGORY_STYLES = {
  create: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  approve: 'border-violet-200 bg-violet-50 text-violet-700',
  payment: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  edit: 'border-blue-200 bg-blue-50 text-blue-700',
  remove: 'border-red-200 bg-red-50 text-red-700',
  add: 'border-cyan-200 bg-cyan-50 text-cyan-700',
  security: 'border-amber-200 bg-amber-50 text-amber-700',
  unknown: 'border-slate-200 bg-slate-50 text-slate-600',
};

const TEXT = {
  en: {
    actionLog: 'Activity', loading: 'Loading activity…', empty: 'No activity has been recorded yet.',
    refresh: 'Refresh activity', description: 'A clear record of changes and document actions.',
    by: 'by', changes: 'changes', previous: 'Previous', new: 'New', technical: 'Technical details',
  },
  ar: {
    actionLog: 'النشاط', loading: 'جارٍ تحميل النشاط…', empty: 'لم يتم تسجيل أي نشاط بعد.',
    refresh: 'تحديث النشاط', description: 'سجل واضح لتغييرات وإجراءات المستند.',
    by: 'بواسطة', changes: 'تغييرات', previous: 'السابق', new: 'الجديد', technical: 'تفاصيل تقنية',
  },
} as const;

function parseTimestamp(value: any) {
  const parsed = value?.toDate?.()
    ?? (value?.seconds ?? value?._seconds
      ? new Date((value.seconds ?? value._seconds) * 1000)
      : new Date(value ?? Date.now()));
  return parsed instanceof Date && !Number.isNaN(parsed.getTime()) ? parsed : new Date();
}

function normalizeLog(log: any, index: number): ActionLogRecord {
  const metadata = log.metadata ?? log.meta_data ?? {};
  return {
    id: String(log.id ?? log.logId ?? log.log_id ?? index),
    action: log.action ?? log.actionType ?? log.action_type,
    module: String(log.module ?? log.moduleName ?? log.module_name ?? 'System'),
    targetName: log.targetName ?? log.target_name ?? undefined,
    userName: String(log.userName ?? log.user_name ?? log.actorName ?? log.actor_name ?? 'System'),
    userEmail: String(log.userEmail ?? log.user_email ?? log.actorEmail ?? log.actor_email ?? ''),
    timestamp: parseTimestamp(log.timestamp ?? log.createdAt ?? log.created_at),
    metadata: metadata && typeof metadata === 'object' ? metadata : {},
  };
}

export default function EntityActionLog({ orgId, entityId, entityType, className, locale = 'en', compact = false }: EntityActionLogProps) {
  const [logs, setLogs] = useState<ActionLogRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const text = TEXT[locale];

  useEffect(() => {
    if (!orgId || !entityId) {
      setLogs([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const token = localStorage.getItem('app_auth_token');

    if (token) {
      const controller = new AbortController();
      const parameters = new URLSearchParams({ targetId: entityId, limit: '250' });
      void fetch(`/api/organizations/${orgId}/activity-logs?${parameters.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
        signal: controller.signal,
      })
        .then(async response => {
          const payload = await response.json().catch(() => []);
          if (!response.ok) throw new Error(payload?.error || 'Could not load the action log');
          return Array.isArray(payload) ? payload : [];
        })
        .then(payload => setLogs(payload.map(normalizeLog).sort((left, right) => right.timestamp.getTime() - left.timestamp.getTime())))
        .catch(error => {
          if (!controller.signal.aborted) {
            console.error('Could not load transaction action log:', error);
            setLogs([]);
          }
        })
        .finally(() => {
          if (!controller.signal.aborted) setLoading(false);
        });
      return () => controller.abort();
    }

    const actionLogQuery = query(
      collection(db, 'activity_logs'),
      where('orgId', '==', orgId),
      where('targetId', '==', entityId),
    );
    return onSnapshot(actionLogQuery, snapshot => {
      setLogs(snapshot.docs
        .map((document, index) => normalizeLog({ id: document.id, ...document.data() }, index))
        .sort((left, right) => right.timestamp.getTime() - left.timestamp.getTime()));
      setLoading(false);
    }, error => {
      console.error('Could not load transaction action log:', error);
      setLogs([]);
      setLoading(false);
    });
  }, [entityId, orgId, refreshKey]);

  const heading = useMemo(() => compact ? text.actionLog : (entityType ? `${entityType} ${text.actionLog}` : text.actionLog), [compact, entityType, text.actionLog]);

  return (
    <section className={cn('space-y-4', className)} aria-label={heading} dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      {!compact && (
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 shrink-0 opacity-50" />
              <h3 className="text-lg font-bold">{heading}</h3>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{text.description}</p>
          </div>
          {localStorage.getItem('app_auth_token') && (
            <button type="button" onClick={() => setRefreshKey(value => value + 1)} className="rounded-full border border-border p-2 hover:bg-muted" aria-label={text.refresh} title={text.refresh}>
              <RefreshCw className="h-4 w-4" />
            </button>
          )}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 rounded-xl border border-dashed border-border py-8 text-xs font-bold text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" /> {text.loading}
        </div>
      ) : logs.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border py-8 text-center text-sm text-muted-foreground">
          <History className="mx-auto mb-2 h-6 w-6" />{text.empty}
        </div>
      ) : (
        <div className="relative space-y-3 before:absolute before:bottom-4 before:start-[15px] before:top-4 before:w-px before:bg-border">
          {logs.map(log => {
            const event = translateActivityEvent({ action: log.action, module: log.module, targetName: log.targetName, metadata: log.metadata }, locale);
            return (
              <article key={log.id} className="relative ps-10">
                <div className="absolute start-0 top-3 z-10 h-8 w-8 rounded-full border border-border bg-background shadow-sm" />
                <div className={cn('rounded-xl border border-border bg-background/80 shadow-sm', compact ? 'p-3' : 'p-4')}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={cn('rounded-full border px-2 py-0.5 text-[10px] font-black tracking-wide', CATEGORY_STYLES[event.category])}>{event.actionLabel}</span>
                        <span className="truncate text-xs font-bold text-foreground">{event.title.replace(`${event.actionLabel}: `, '')}</span>
                      </div>
                      {event.summary && <p className="mt-2 text-xs leading-relaxed text-muted-foreground">{event.summary}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px] text-muted-foreground">
                        <User className="h-3 w-3" /><span>{text.by}</span><span className="font-semibold text-foreground">{log.userName}</span>
                      </div>
                    </div>
                    <time className="flex items-center gap-1 whitespace-nowrap text-[10px] text-muted-foreground" dateTime={log.timestamp.toISOString()}>
                      <Clock className="h-3 w-3" /> {log.timestamp.toLocaleString(locale === 'ar' ? 'ar' : 'en')}
                    </time>
                  </div>

                  {event.facts.length > 0 && (
                    <dl className="mt-3 grid grid-cols-2 gap-2 rounded-lg bg-muted/60 p-2 text-xs">
                      {event.facts.map(fact => <div key={fact.label} className="min-w-0"><dt className="text-[9px] font-bold text-muted-foreground">{fact.label}</dt><dd className="truncate font-semibold">{fact.value}</dd></div>)}
                    </dl>
                  )}

                  {event.changes.length > 0 && (
                    <details className="group mt-3 rounded-lg border border-border bg-muted/30 p-2">
                      <summary className="flex cursor-pointer list-none items-center justify-between text-[10px] font-bold text-muted-foreground">
                        <span>{event.changes.length} {text.changes}</span><ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
                      </summary>
                      <div className="mt-2 space-y-2">
                        {event.changes.map(change => (
                          <div key={change.field} className="rounded-md bg-background p-2 text-xs">
                            <div className="mb-1 font-bold">{change.label}</div>
                            <div className="grid grid-cols-2 gap-2">
                              <div className="min-w-0"><span className="text-[9px] text-muted-foreground">{text.previous}</span><p className="break-words text-red-700">{change.before}</p></div>
                              <div className="min-w-0"><span className="text-[9px] text-muted-foreground">{text.new}</span><p className="break-words text-emerald-700">{change.after}</p></div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </details>
                  )}

                  {!event.isKnown && Object.keys(log.metadata).length > 0 && (
                    <details className="mt-2 text-[10px] text-muted-foreground">
                      <summary className="cursor-pointer">{text.technical}</summary>
                      <pre className="mt-2 max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-md bg-muted p-2" dir="ltr">{JSON.stringify(log.metadata, null, 2)}</pre>
                    </details>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      )}
    </section>
  );
}
